"use client";
import { getMediaUrl } from "@/lib/utils";
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import { getCareers } from "@/lib/api";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";

const CareersPage = () => {
  const [careersData, setCareersData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCareers("ar");
        console.log("Careers Data", data);
        setCareersData(data);
        // Wait for state to update, then trigger render
        setTimeout(() => setShouldRender(true), 50);
      } catch (error) {
        console.error("Failed to fetch careers data:", error);
        setCareersData(null);
        setTimeout(() => setShouldRender(true), 50);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading || !shouldRender) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0a367b]/20 border-t-[#0a367b] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#0a367b] text-lg">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-white"
      dir="rtl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Navbar />
      <HeroSection data={careersData?.hero} />
      <TeamSection data={careersData?.team} />
      <CultureSection data={careersData?.culture} />
      <BenefitsSection data={careersData?.benefits} />
      <JobsSection
        section={
          careersData?.jobsSection ||
          (!Array.isArray(careersData?.jobs) ? careersData?.jobs : undefined)
        }
        jobs={Array.isArray(careersData?.jobs) ? careersData?.jobs : []}
      />
      <SocialSection />
      <Footer />
    </motion.div>
  );
};

// Rest of your components remain the same...

const HeroSection = ({ data }) => {
  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "انضم إلى فريقنا");
  const titleHighlight = getLocalizedValue(
    data?.titleHighlight,
    "ودعنا نعمل معًا"
  );
  const subtitle = getLocalizedValue(
    data?.subtitle,
    "استكشف الوظائف المتاحة\nالمستقبل المجتمع التكريم"
  );
  const videoUrl = data?.video?.url || data?.videoUrl;

  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);
  const [floatingParticles, setFloatingParticles] = useState([]);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const particles = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 6 + Math.random() * 4,
    }));
    setFloatingParticles(particles);
  }, []);

  return (
    <motion.section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ y: heroY, opacity: heroOpacity }}
    >
      {/* Background Video or Image */}
      <div className="absolute inset-0 will-change-transform">
        {videoUrl ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={getMediaUrl("arabic.jpg")}
            className="w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        ) : (
          <div
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url('${getMediaUrl("arabic.jpg")}')` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
      </div>

      {/* Floating Particles */}
      {isMounted && (
        <div className="absolute inset-0">
          {floatingParticles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
              style={{
                top: `${particle.top}%`,
                left: `${particle.left}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: particle.duration,
                repeat: Infinity,
                delay: particle.delay,
              }}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 1 }}
          >
            {title}
            <br />
            <span className="text-[#dfa428]">{titleHighlight}</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl leading-relaxed opacity-90 mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            {subtitle.split("\n").map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                {idx < subtitle.split("\n").length - 1 && <br />}
              </React.Fragment>
            ))}
          </motion.p>
        </motion.div>
      </div>

      {/* Geometric Shapes */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
    </motion.section>
  );
};
const TeamSection = ({ data }) => {
  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "ما يميزنا");
  const description = getLocalizedValue(
    data?.description,
    "ما يميزنا هو ما يجمعنا معًا - شغف مشترك لحل تحديات الأعمال من خلال الإستراتيجية والتصميم والهندسة. نحن مجموع فريقنا ووجهات نظرهم الفريدة وقدراتهم وقدرتنا على التعاون من التفكير إلى النشر."
  );
  const images = data?.images || [];

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 gap-4 max-w-4xl mx-auto">
          {(images.length > 0 ? images : [1, 2, 3, 4]).map((item, index) => {
            const imageUrl =
              typeof item === "string"
                ? item
                : item?.url || `/team-${index + 1}`;
            return (
              <motion.div
                key={index}
                className="relative overflow-hidden rounded-2xl shadow-xl"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 2 : -2 }}
              >
                <Image
                  fill
                  src={imageUrl}
                  alt={`Team ${index + 1}`}
                  className="w-full h-48 md:h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a367b]/30 to-transparent"></div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CultureSection = ({ data }) => {
  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "ثقافة العمل لدينا");
  const description = getLocalizedValue(
    data?.description,
    "نؤمن ببناء بيئة عمل محفزة ومبدعة تساعد كل فرد في الفريق على تحقيق أقصى إمكاناته"
  );
  const values =
    Array.isArray(data?.values) && data.values.length > 0
      ? data.values
      : [
          {
            title: "الرؤية الواضحة",
            description: "نؤمن بوضع أهداف واضحة والعمل بجد لتحقيقها",
          },
          {
            title: "العمل الجماعي",
            description: "نحقق النجاح من خلال التعاون والعمل كفريق واحد",
          },
          {
            title: "الإبداع والابتكار",
            description: "نشجع الأفكار الجديدة والحلول المبتكرة",
          },
          {
            title: "النمو المستمر",
            description: "نستثمر في تطوير مهارات فريقنا باستمرار",
          },
        ];

  const sectionRef = useRef(null);
  const [particles, setParticles] = useState([]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  const isInView = useInView(sectionRef, { once: true });

  useEffect(() => {
    const generatedParticles = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setParticles(generatedParticles);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0C0927 0%, #233C78 100%)",
      }}
    >
      {/* Animated Background */}
      <motion.div className="absolute inset-0" style={{ y: backgroundY }}>
        {/* Floating Particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-3 h-3 bg-[#dfa428]/30 rounded-full"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.2, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
            }}
          />
        ))}

        {/* Background Shapes */}
        <motion.div
          className="absolute top-20 right-20 w-48 h-48 bg-[#dfa428]/10 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-36 h-36 bg-white/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-6"
        style={{ y: contentY }}
      >
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl font-bold text-white mb-6">{title}</h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            {description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 100 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <motion.div
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 hover:bg-white/20 transition-all duration-500"
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <motion.div
                  className="text-6xl mb-6"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.5,
                  }}
                >
                  {value.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-4">
                  {getLocalizedValue(value.title)}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {getLocalizedValue(value.description)}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const BenefitsSection = ({ data }) => {
  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(data?.title, "الامتيازات والمزايا");
  const description = getLocalizedValue(
    data?.description,
    "نريدك أن تحب العمل هنا بقدر ما نحب. لإلهام فريقنا ليكون مبدعًا ومنتِجًا والأهم من ذلك - الشعور بالتقدير كموظفين ، نقدم امتيازات لا حصر لها ومزيلات للضغط. كم هو ممتع الاسترخاء أثناء العمل!"
  );
  const benefits =
    Array.isArray(data?.items) && data.items.length > 0
      ? data.items
      : [
          {
            title: "Vacation & Paid Time Off",
            description: "إجازات مدفوعة الأجر ووقت راحة كافي",
          },
          {
            title: "Work-life Integration",
            description: "توازن صحي بين العمل والحياة الشخصية",
          },
          {
            title: "Maternity/Paternity Benefits",
            description: "مزايا إجازة الأمومة والأبوة",
          },
          {
            title: "Personal Career Growth",
            description: "فرص نمو وتطوير مهني مستمر",
          },
          {
            title: "Learning & Development",
            description: "برامج تدريب وتطوير المهارات",
          },
          {
            title: "Healthy Food & Snacks",
            description: "طعام صحي ووجبات خفيفة مجانية",
          },
        ];

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/5 to-[#0a367b]/5 rounded-full"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-[#0a367b]/5 to-[#dfa428]/5 rounded-full"
          animate={{
            rotate: [360, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 15, repeat: Infinity }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 100 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <motion.div
                className="bg-gray-50 rounded-3xl p-8 text-center hover:bg-yellow-50 transition-all duration-500 hover:shadow-xl"
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <motion.div
                  className="text-5xl mb-6"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                >
                  {benefit.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-[#0a367b] mb-4">
                  {getLocalizedValue(benefit.title)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {getLocalizedValue(benefit.description)}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JobsSection = ({ section, jobs }) => {
  console.log("section", section);
  console.log("jobs", jobs);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  const jobValue = (value) => getLocalizedValue(value, "");

  // Fallback to hardcoded values if data is not available
  const title = getLocalizedValue(section?.title, "الوظائف المتاحة");
  const description = getLocalizedValue(
    section?.description,
    "استكشف الفرص المتاحة للانضمام إلى فريقنا"
  );
  const message = getLocalizedValue(
    section?.message,
    "حاليا ليس لدينا وظائف متاحة"
  );

  const jobListings = Array.isArray(jobs) ? jobs : [];
  const hasJobs = jobListings.length > 0;

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedJob(null), 300);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6">
            {title}
          </h2>
          <p className="text-xl text-gray-600">{description}</p>
        </motion.div>

        {hasJobs ? (
          <motion.div
            className="flex flex-wrap gap-4 justify-center items-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            {jobListings.map((job, index) => (
              <motion.div
                key={job.id || index}
                className="bg-white rounded-3xl  px-6  md:px-14 py-8 shadow-xl border border-slate-100 grid grid-cols-12 gap-4  cursor-pointer "
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ y: -6, scale: 1.02 }}
                onClick={() => handleJobClick(job)}
              >
                <div className="w-full col-span-12 md:col-span-2 mb-5 md:mb-0">
                  <img className="w-full object-cover rounded-lg" src={job.image.url} alt="" srcset="" />
                </div>
                <div className="col-span-12 md:col-span-10 ml-5 ">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-2xl font-bold text-[#0a367b]">
                    {jobValue(job.title) || "وظيفة جديدة"}
                  </h3>
                  {jobValue(job.type) && (
                    <span className="inline-flex items-center justify-center rounded-full bg-[#0a367b]/10 text-[#0a367b] px-4 py-1 text-sm font-semibold">
                      {jobValue(job.type)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {jobValue(job.department) && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#0a367b]">القسم:</span>
                      {jobValue(job.department)}
                    </div>
                  )}
                  {jobValue(job.location) && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#0a367b]">الموقع:</span>
                      {jobValue(job.location)}
                    </div>
                  )}
                </div>
                {jobValue(job.description) && (
                  <p className="text-gray-600 leading-relaxed line-clamp-3">
                    {jobValue(job.description)}
                  </p>
                )}
                <div className="flex items-center gap-2 text-[#dfa428] font-semibold mt-2">
                  <span>عرض التفاصيل</span>
                  <span className="text-xl">←</span>
                </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-12 shadow-xl"
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <motion.div
                className="text-8xl mb-8"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                💼
              </motion.div>
              <h3 className="text-3xl font-bold text-[#0a367b] mb-6">
                {message}
              </h3>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Job Details Modal */}
      {isModalOpen && selectedJob && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <motion.div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-6 left-6 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-300 z-10"
            >
              <span className="text-2xl text-gray-600">×</span>
            </button>

            {/* Job Image */}
            {selectedJob.image?.url && (
              <div className="relative w-full h-64 rounded-t-3xl overflow-hidden ">
                <Image
                  src={selectedJob.image.url}
                  alt={jobValue(selectedJob.title)}
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            )}

            {/* Modal Content */}
            <div className="p-8 space-y-6">
              {/* Title and Type */}
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <h2 className="text-3xl md:text-4xl font-bold text-[#0a367b]">
                  {jobValue(selectedJob.title) || "وظيفة جديدة"}
                </h2>
                {jobValue(selectedJob.type) && (
                  <span className="inline-flex items-center justify-center rounded-full bg-[#0a367b]/10 text-[#0a367b] px-6 py-2 text-sm font-semibold">
                    {jobValue(selectedJob.type)}
                  </span>
                )}
              </div>

              {/* Location and Department */}
              <div className="flex flex-wrap gap-6 text-gray-700">
                {jobValue(selectedJob.location) && (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📍</span>
                    <div>
                      <div className="text-sm text-gray-500">الموقع</div>
                      <div className="font-semibold">
                        {jobValue(selectedJob.location)}
                      </div>
                    </div>
                  </div>
                )}
                {jobValue(selectedJob.department) && (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💼</span>
                    <div>
                      <div className="text-sm text-gray-500">القسم</div>
                      <div className="font-semibold">
                        {jobValue(selectedJob.department)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {jobValue(selectedJob.description) && (
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-[#0a367b]">
                    عن الوظيفة
                  </h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {jobValue(selectedJob.description)}
                  </p>
                </div>
              )}

              {/* Apply Button */}
              <motion.a
                href="mailto:info@palmofbabylon.com"
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#dfa428] text-[#0a367b] px-8 py-4 font-bold text-lg hover:bg-[#dfa428]/90 transition-colors duration-300 shadow-lg"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>📧</span>
                <span>قدّم على الوظيفة</span>
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

const SocialSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const socialLinks = [
    { name: "لينكد إن", icon: "💼", color: "from-blue-500 to-blue-700" },
    { name: "انستغرام", icon: "📷", color: "from-pink-500 to-purple-600" },
    { name: "فيسبوك", icon: "📘", color: "from-blue-600 to-blue-800" },
    { name: "يوتيوب", icon: "📺", color: "from-red-500 to-red-700" },
  ];

  return <></>;
};

export default CareersPage;
